import { useAtomValue } from 'jotai';

import { Stack } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { FicoScoreDisplay } from '@common/components/FicoScoreDisplay';
import { InfoLineItem } from '@common/components/InfoLineItem';
import { TextCell } from '@common/components/TextCell';
import { formatCurrency } from '@common/lib/currency';

import { useGetOrderPaymentFai } from '../../api/useGetOrderPaymentFai';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';
import { SectionSkeleton } from '../order-details/shared/SectionSkeleton';
import { FinanceAndInsuranceProfitCell, PaymentChoiceCell, TotalCell } from '../table-cells';
import { FinancingStatusTag } from '../table-cells/shared/FinancingStatusTag';
import { PaymentStatusTag } from '../table-cells/shared/PaymentStatusTag';


const SECTION_TITLE = 'Overview';

export const OverviewSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderPaymentFai, isLoading } = useGetOrderPaymentFai(
		selectedRow?.purchasedVehicleId || undefined,
	);

	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!orderPaymentFai || !selectedRow) {
		return null;
	}

	const { overview } = orderPaymentFai;

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<Stack itemsSpacing='16'>
				<InfoLineItem
					title='Total'
					content={
						<TotalCell
							total={overview.total ? formatCurrency(overview.total) : undefined}
						/>
					}
					tooltip='Includes the vehicle price and any applicable PG&A, taxes, fees, and F&I upsells.'
				/>
				<InfoLineItem
					title='F&I PRU'
					content={
						<FinanceAndInsuranceProfitCell
							profit={
								overview.faiPru !== undefined
									? formatCurrency(overview.faiPru)
									: undefined
							}
						/>
					}
				/>
				<InfoLineItem
					title='Pre-qualification'
					content={<TextCell value={overview.preQualification} />}
				/>
				<InfoLineItem
					title='Payment choice'
					content={<PaymentChoiceCell paymentChoice={overview.paymentChoice} />}
				/>
				<InfoLineItem
					title='Payment status'
					content={<PaymentStatusTag status={overview.paymentStatus} />}
				/>
				<InfoLineItem
					title='Financing status'
					content={<FinancingStatusTag status={overview.financingStatus} />}
				/>
				{overview.ficoScore !== undefined && (
					<InfoLineItem
						title='FICO score'
						content={<FicoScoreDisplay ficoScore={overview.ficoScore} />}
					/>
				)}
			</Stack>
		</AccordionSection>
	);
};
